const aliasColumnCache = new Map();
const vendorDirectoryCache = new Map();
const vendorDirectoryInflight = new Map();
const aiVendorMatchCache = new Map();

const PRICE_AI_ENDPOINT = () => `${(typeof window !== 'undefined' && window.SUPABASE_URL) || ''}/functions/v1/price-update-ai`;

async function getEdgeAuthHeaders() {
  if (typeof window === 'undefined') return null;
  try {
    if (window.supabase?.auth?.getSession) {
      const { data } = await window.supabase.auth.getSession();
      const token = data?.session?.access_token;
      if (token) {
        return {
          Authorization: `Bearer ${token}`,
          apikey: window.SUPABASE_KEY,
          'Content-Type': 'application/json'
        };
      }
    }
  } catch (_) {}
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith('sb-') || !key.endsWith('-auth-token')) continue;
      const parsed = JSON.parse(localStorage.getItem(key) || 'null');
      if (parsed?.access_token) {
        return {
          Authorization: `Bearer ${parsed.access_token}`,
          apikey: window.SUPABASE_KEY,
          'Content-Type': 'application/json'
        };
      }
    }
  } catch (_) {}
  return null;
}

function buildExistingVendorsForAI(directory) {
  const aliasesByVendor = new Map();
  (directory?.aliases || []).forEach((row) => {
    const list = aliasesByVendor.get(String(row.vendor_id)) || [];
    if (row.alias) list.push(row.alias);
    aliasesByVendor.set(String(row.vendor_id), list);
  });
  return (directory?.vendors || []).map((v) => ({
    id: String(v.id),
    displayName: v.display_name,
    aliases: aliasesByVendor.get(String(v.id)) || []
  }));
}

function fingerprintExistingVendors(existingVendors) {
  // Stable, content-aware fingerprint so cache invalidates when any vendor
  // or alias is added/removed (not just when the list length changes).
  const tokens = existingVendors
    .map((v) => `${v.id}|${(v.aliases || []).slice().sort().join(',')}`)
    .sort();
  return `${existingVendors.length}:${tokens.join(';')}`;
}

// Gate for acting on an AI vendor match. 'high' is required, and when the
// endpoint reports a calibrated confidencePct it must agree (>= 80) — a model
// that says "high" but 60% is telling on itself.
export function isConfidentVendorMatch(aiResult) {
  if (!aiResult?.vendorId || aiResult.confidence !== 'high') return false;
  if (aiResult.confidencePct == null) return true;
  const pct = Number(aiResult.confidencePct);
  return !Number.isFinite(pct) || pct >= 80;
}

async function aiMatchVendor({ candidateName, vendorDirectory, storeId = null }) {
  const cleanName = normalizeVendorName(candidateName);
  if (!cleanName) return null;
  const existingVendors = buildExistingVendorsForAI(vendorDirectory);
  if (!existingVendors.length) return null;

  const cacheKey = [
    storeId == null ? 'no-store' : `store:${storeId}`,
    normalizeVendorMatchKey(cleanName),
    fingerprintExistingVendors(existingVendors)
  ].join('::');
  if (aiVendorMatchCache.has(cacheKey)) return aiVendorMatchCache.get(cacheKey);

  const endpoint = PRICE_AI_ENDPOINT();
  if (!endpoint || endpoint.startsWith('/functions')) return null;

  try {
    const headers = await getEdgeAuthHeaders();
    if (!headers) return null;
    const resp = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        action: 'match_vendor',
        candidateName: cleanName,
        existingVendors
      })
    });
    if (!resp.ok) {
      console.warn('[aiMatchVendor] non-OK response:', resp.status);
      return null;
    }
    const data = await resp.json();
    aiVendorMatchCache.set(cacheKey, data);
    return data;
  } catch (err) {
    console.warn('[aiMatchVendor] failed:', err?.message);
    return null;
  }
}

function clearAiVendorMatchCache() {
  aiVendorMatchCache.clear();
}

const compactWhitespace = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

const stripDiacritics = (value) => String(value ?? '')
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '');

export const normalizeVendorName = (value) => compactWhitespace(value);

const normalizeVendorMatchKey = (value) => stripDiacritics(normalizeVendorName(value))
  .toLowerCase()
  .replace(/&/g, ' and ')
  .replace(/[^\w\s]+/g, ' ')
  .replace(/_/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const isDuplicateError = (error) => String(error?.code || '') === '23505';

// The provenance columns (migration 20260817140000) may be absent in older
// environments. PostgREST reports an unknown payload column as PGRST204
// ("could not find the column in the schema cache"); raw Postgres says 42703.
// Either way the alias matters more than its paper trail — retry bare.
const isMissingColumnError = (error) => {
  const code = String(error?.code || '');
  if (code === '42703' || code === 'PGRST204') return true;
  const message = String(error?.message || '');
  return /column/i.test(message) && /does not exist|schema cache/i.test(message);
};

const parseStoreId = (storeId) => {
  const parsed = Number(storeId);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const vendorCacheKey = (storeId) => `store:${storeId}`;

const createDirectory = (vendors = [], aliases = []) => {
  const vendorById = new Map();
  const exactVendorByKey = new Map();
  const aliasByKey = new Map();

  vendors.forEach((vendor) => {
    const displayName = normalizeVendorName(vendor?.display_name);
    if (!displayName) return;
    const normalizedDisplay = normalizeVendorMatchKey(displayName);
    const normalizedOriginal = normalizeVendorMatchKey(vendor?.original_name);
    const safeVendor = {
      id: vendor.id,
      store_id: vendor.store_id,
      display_name: displayName,
      original_name: normalizeVendorName(vendor?.original_name),
      status: vendor?.status || 'active'
    };
    vendorById.set(String(vendor.id), safeVendor);
    if (normalizedDisplay && !exactVendorByKey.has(normalizedDisplay)) exactVendorByKey.set(normalizedDisplay, safeVendor);
    if (normalizedOriginal && !exactVendorByKey.has(normalizedOriginal)) exactVendorByKey.set(normalizedOriginal, safeVendor);
  });

  aliases.forEach((aliasRow) => {
    const aliasName = normalizeVendorName(aliasRow?.alias_name || aliasRow?.alias);
    if (!aliasName) return;
    const key = normalizeVendorMatchKey(aliasName);
    if (!key || aliasByKey.has(key)) return;
    const vendor = vendorById.get(String(aliasRow.vendor_id));
    if (!vendor) return;
    aliasByKey.set(key, {
      alias: aliasName,
      vendor
    });
  });

  return {
    vendors: [...vendorById.values()],
    aliases: aliases.map((aliasRow) => ({
      ...aliasRow,
      alias: normalizeVendorName(aliasRow?.alias_name || aliasRow?.alias)
    })),
    exactVendorByKey,
    aliasByKey
  };
};

async function detectAliasColumn(supabaseClient) {
  if (!supabaseClient) return null;
  if (aliasColumnCache.has('vendor_aliases')) return aliasColumnCache.get('vendor_aliases');

  const aliasProbe = await supabaseClient.from('vendor_aliases').select('id, alias').limit(1);
  if (!aliasProbe.error) {
    aliasColumnCache.set('vendor_aliases', 'alias');
    return 'alias';
  }

  const aliasNameProbe = await supabaseClient.from('vendor_aliases').select('id, alias_name').limit(1);
  if (!aliasNameProbe.error) {
    aliasColumnCache.set('vendor_aliases', 'alias_name');
    return 'alias_name';
  }

  aliasColumnCache.set('vendor_aliases', null);
  return null;
}

async function fetchVendorsForStore({ supabaseClient, parsedStoreId }) {
  let vendorRows = [];
  let vendorError = null;

  ({ data: vendorRows, error: vendorError } = await supabaseClient
    .from('vendors')
    .select('id, store_id, display_name, original_name, status')
    .eq('store_id', parsedStoreId));

  if (vendorError) {
    ({ data: vendorRows, error: vendorError } = await supabaseClient
      .from('vendors')
      .select('id, store_id, display_name, original_name')
      .eq('store_id', parsedStoreId));
  }

  if (vendorError) throw vendorError;
  return (vendorRows || []).filter((row) => String(row?.status || '').toLowerCase() !== 'merged');
}

async function fetchAliasesForStore({ supabaseClient, parsedStoreId, aliasColumn }) {
  if (!aliasColumn) return [];
  const { data, error } = await supabaseClient
    .from('vendor_aliases')
    .select(`id, vendor_id, ${aliasColumn}`)
    .eq('store_id', parsedStoreId);
  if (error) return [];
  return (data || []).map((row) => ({
    ...row,
    alias: row[aliasColumn]
  }));
}

const resolveFromDirectory = (directory, vendorNameInput) => {
  const inputName = normalizeVendorName(vendorNameInput);
  const key = normalizeVendorMatchKey(inputName);
  if (!key) return null;

  const vendorMatch = directory.exactVendorByKey.get(key);
  if (vendorMatch) {
    return {
      matchType: 'vendor',
      matchedText: vendorMatch.display_name,
      vendorId: vendorMatch.id,
      vendorDisplayName: vendorMatch.display_name
    };
  }

  const aliasMatch = directory.aliasByKey.get(key);
  if (aliasMatch?.vendor) {
    return {
      matchType: 'alias',
      matchedText: aliasMatch.alias,
      vendorId: aliasMatch.vendor.id,
      vendorDisplayName: aliasMatch.vendor.display_name
    };
  }

  return null;
};

export async function fetchVendorDirectory({ supabaseClient, storeId }) {
  const parsedStoreId = parseStoreId(storeId);
  if (!supabaseClient || !parsedStoreId) {
    return createDirectory([], []);
  }

  const aliasColumn = await detectAliasColumn(supabaseClient);
  const vendors = await fetchVendorsForStore({ supabaseClient, parsedStoreId });
  const aliases = await fetchAliasesForStore({ supabaseClient, parsedStoreId, aliasColumn });
  return createDirectory(vendors, aliases);
}

export function clearVendorDirectoryCache(storeId = null) {
  // Whenever the vendor directory changes, invalidate the AI-match cache too
  // so subsequent matches see the freshly added vendor or alias.
  clearAiVendorMatchCache();
  if (storeId == null) {
    vendorDirectoryCache.clear();
    vendorDirectoryInflight.clear();
    return;
  }
  const parsedStoreId = parseStoreId(storeId);
  if (!parsedStoreId) return;
  const key = vendorCacheKey(parsedStoreId);
  vendorDirectoryCache.delete(key);
  vendorDirectoryInflight.delete(key);
}

export async function getCachedVendorDirectory({
  supabaseClient,
  storeId,
  forceRefresh = false,
  maxAgeMs = 30_000
}) {
  const parsedStoreId = parseStoreId(storeId);
  if (!supabaseClient || !parsedStoreId) return createDirectory([], []);
  const key = vendorCacheKey(parsedStoreId);
  const cached = vendorDirectoryCache.get(key);
  const now = Date.now();
  if (!forceRefresh && cached && now - cached.at <= maxAgeMs) {
    return cached.value;
  }
  if (!forceRefresh) {
    const inflight = vendorDirectoryInflight.get(key);
    if (inflight) return inflight;
  }

  const fetchPromise = fetchVendorDirectory({ supabaseClient, storeId: parsedStoreId })
    .then((value) => {
      vendorDirectoryCache.set(key, { at: Date.now(), value });
      vendorDirectoryInflight.delete(key);
      return value;
    })
    .catch((error) => {
      vendorDirectoryInflight.delete(key);
      throw error;
    });

  vendorDirectoryInflight.set(key, fetchPromise);
  const value = await fetchPromise;
  vendorDirectoryCache.set(key, { at: now, value });
  return value;
}

async function insertVendorAliasIfNeeded({
  supabaseClient,
  parsedStoreId,
  vendorId,
  aliasName,
  provenance = null
}) {
  const cleanAlias = normalizeVendorName(aliasName);
  if (!cleanAlias) return;
  const aliasColumn = await detectAliasColumn(supabaseClient);
  if (!aliasColumn) return;

  const payload = {
    store_id: parsedStoreId,
    vendor_id: vendorId,
    [aliasColumn]: cleanAlias
  };

  // Provenance (migration 20260817140000): which door taught this spelling,
  // and on whose word. An alias silently reroutes every future arrival of its
  // name, so a bad matcher run must stay findable and revertable. The CHECK
  // constraint permits match_confidence only on ai_match rows.
  const source = provenance?.source || null;
  const stamped = source ? {
    ...payload,
    alias_source: source,
    alias_channel: provenance?.channel ?? null,
    match_confidence: source === 'ai_match' && Number.isFinite(Number(provenance?.confidencePct))
      ? Number(provenance.confidencePct)
      : null,
    created_by: provenance?.userId ?? null
  } : payload;

  // Use upsert on (store_id, normalized_key) — the actual unique index on
  // vendor_aliases. The normalized_key column is computed by a DB trigger from
  // alias_name/alias, so we don't need to include it in the payload; we just
  // tell PostgREST which constraint to use for conflict resolution. This
  // replaces the old .insert() + 409-swallow pattern, and also handles stale
  // aliases that point to an old vendor_id after a chain of renames
  // (e.g. Sysco → Sysco LA → Sysco Los Angeles).
  let { error } = await supabaseClient
    .from('vendor_aliases')
    .upsert([stamped], { onConflict: 'store_id,normalized_key' });
  if (error && stamped !== payload && isMissingColumnError(error)) {
    // Provenance columns not migrated here — never fail a save over them.
    ({ error } = await supabaseClient
      .from('vendor_aliases')
      .upsert([payload], { onConflict: 'store_id,normalized_key' }));
  }
  if (error) {
    console.warn('Failed to upsert vendor alias:', error.message, error.code);
  }
}

export async function createVendorAliasIfNeeded({
  supabaseClient,
  storeId,
  vendorId,
  aliasName,
  channel = null,
  userId = null
}) {
  const parsedStoreId = parseStoreId(storeId);
  if (!supabaseClient || !parsedStoreId || !vendorId) return false;
  // Callers of this export are editor surfaces where a person chose the
  // vendor by hand — 'user_manual', not a matcher's verdict.
  await insertVendorAliasIfNeeded({
    supabaseClient,
    parsedStoreId,
    vendorId,
    aliasName,
    provenance: { source: 'user_manual', channel, userId }
  });
  return true;
}

async function getVendorById({ supabaseClient, vendorId, parsedStoreId }) {
  if (!vendorId) return null;
  const { data, error } = await supabaseClient
    .from('vendors')
    .select('id, display_name, original_name, store_id')
    .eq('id', vendorId)
    .eq('store_id', parsedStoreId)
    .single();
  if (error || !data) return null;
  return data;
}

export async function resolveVendorByNameOrAlias({
  supabaseClient,
  storeId,
  vendorName,
  stubNote = 'Auto-created vendor',
  createIfMissing = true,
  vendorDirectory = null,
  useAI = false,
  channel = null,
  userId = null
}) {
  const parsedStoreId = parseStoreId(storeId);
  const cleanName = normalizeVendorName(vendorName);

  if (!supabaseClient || !parsedStoreId || !cleanName) {
    return null;
  }

  const directory = vendorDirectory || await getCachedVendorDirectory({ supabaseClient, storeId: parsedStoreId });
  const existing = resolveFromDirectory(directory, cleanName);
  if (existing) return existing;
  if (!createIfMissing) return null;

  // AI deduplication: before creating anything, ask AI if this candidate
  // is a name variant of an existing vendor in the store. Only a high-confidence
  // verdict may write a durable alias: an alias silently reroutes every future
  // import of that name, so a wrong merge is corruption (two businesses fused,
  // items and options rewired), while a wrong split is a vendor merge away from
  // fixed. Medium-confidence matches fall through to creating a separate vendor.
  if (useAI) {
    const aiResult = await aiMatchVendor({ candidateName: cleanName, vendorDirectory: directory, storeId: parsedStoreId });
    if (isConfidentVendorMatch(aiResult)) {
      const vendorRow = await getVendorById({ supabaseClient, vendorId: aiResult.vendorId, parsedStoreId });
      if (vendorRow?.id) {
        await insertVendorAliasIfNeeded({
          supabaseClient,
          parsedStoreId,
          vendorId: vendorRow.id,
          aliasName: cleanName,
          provenance: { source: 'ai_match', channel, confidencePct: aiResult.confidencePct, userId }
        });
        clearVendorDirectoryCache(parsedStoreId);
        return {
          matchType: 'ai_alias',
          matchedText: cleanName,
          vendorId: vendorRow.id,
          vendorDisplayName: normalizeVendorName(vendorRow.display_name) || cleanName,
          aiReason: aiResult.reason || ''
        };
      }
    }
  }

  try {
    const { data: resolvedId, error } = await supabaseClient.rpc('resolve_vendor', {
      p_store_id: parsedStoreId,
      p_vendor_name: cleanName
    });
    if (!error && resolvedId) {
      const vendorRow = await getVendorById({ supabaseClient, vendorId: resolvedId, parsedStoreId });
      if (vendorRow?.id) {
        await insertVendorAliasIfNeeded({
          supabaseClient,
          parsedStoreId,
          vendorId: vendorRow.id,
          aliasName: cleanName,
          provenance: { source: 'self_seed', channel, userId }
        });
        return {
          matchType: 'created_or_resolved',
          matchedText: cleanName,
          vendorId: vendorRow.id,
          vendorDisplayName: normalizeVendorName(vendorRow.display_name) || cleanName
        };
      }
    }
  } catch (rpcError) {
    console.warn('resolve_vendor rpc unavailable, falling back to client resolver:', rpcError?.message);
  }

  return createVendorIfNeeded({
    supabaseClient,
    storeId: parsedStoreId,
    vendorName: cleanName,
    stubNote,
    vendorDirectory: directory,
    channel,
    userId
  });
}

export async function createVendorIfNeeded({
  supabaseClient,
  storeId,
  vendorName,
  stubNote = 'Auto-created vendor',
  vendorDirectory = null,
  channel = null,
  userId = null
}) {
  const parsedStoreId = parseStoreId(storeId);
  const cleanName = normalizeVendorName(vendorName);
  if (!supabaseClient || !parsedStoreId || !cleanName) return null;

  const directory = vendorDirectory || await getCachedVendorDirectory({ supabaseClient, storeId: parsedStoreId });
  const existing = resolveFromDirectory(directory, cleanName);
  if (existing) return existing;

  const payload = {
    store_id: parsedStoreId,
    display_name: cleanName,
    original_name: cleanName,
    status: 'active',
    notes: stubNote
  };

  let createdVendor = null;
  let createError = null;
  ({ data: createdVendor, error: createError } = await supabaseClient
    .from('vendors')
    .insert([payload])
    .select('id, display_name')
    .single());

  if (createError) {
    console.warn('Vendor insert failed, retrying via lookup:', createError.message);
    const reloadedDirectory = await getCachedVendorDirectory({
      supabaseClient,
      storeId: parsedStoreId,
      forceRefresh: true
    });
    const matched = resolveFromDirectory(reloadedDirectory, cleanName);
    if (matched) return matched;
    return null;
  }

  await insertVendorAliasIfNeeded({
    supabaseClient,
    parsedStoreId,
    vendorId: createdVendor.id,
    aliasName: cleanName,
    provenance: { source: 'self_seed', channel, userId }
  });
  clearVendorDirectoryCache(parsedStoreId);

  return {
    matchType: 'created',
    matchedText: cleanName,
    vendorId: createdVendor.id,
    vendorDisplayName: normalizeVendorName(createdVendor.display_name) || cleanName
  };
}

export async function assignVendorToItem({
  supabaseClient,
  storeId,
  vendorName,
  stubNote = 'Auto-created from item assignment',
  vendorDirectory = null,
  useAI = false,
  channel = null,
  userId = null
}) {
  const cleanName = normalizeVendorName(vendorName);
  if (!cleanName) {
    return {
      vendorId: null,
      vendorDisplayName: null,
      matchType: 'empty'
    };
  }

  const resolved = await resolveVendorByNameOrAlias({
    supabaseClient,
    storeId,
    vendorName: cleanName,
    stubNote,
    createIfMissing: true,
    vendorDirectory,
    useAI,
    channel,
    userId
  });

  if (!resolved?.vendorId) {
    return {
      vendorId: null,
      vendorDisplayName: cleanName,
      matchType: 'unresolved'
    };
  }

  return {
    vendorId: resolved.vendorId,
    vendorDisplayName: resolved.vendorDisplayName || cleanName,
    matchType: resolved.matchType || 'vendor'
  };
}

export async function renameVendorAndCreateAlias({
  supabaseClient,
  storeId,
  vendorId,
  newDisplayName,
  channel = null,
  userId = null
}) {
  const parsedStoreId = parseStoreId(storeId);
  const cleanName = normalizeVendorName(newDisplayName);
  if (!supabaseClient || !parsedStoreId || !vendorId || !cleanName) {
    throw new Error('Missing vendor rename inputs.');
  }

  const { data: currentVendor, error: currentVendorError } = await supabaseClient
    .from('vendors')
    .select('id, store_id, display_name')
    .eq('id', vendorId)
    .eq('store_id', parsedStoreId)
    .single();

  if (currentVendorError || !currentVendor) {
    throw new Error(currentVendorError?.message || 'Vendor not found.');
  }

  const oldName = normalizeVendorName(currentVendor.display_name);
  if (normalizeVendorMatchKey(oldName) === normalizeVendorMatchKey(cleanName)) {
    return currentVendor;
  }

  const conflictingVendor = await resolveVendorByNameOrAlias({
    supabaseClient,
    storeId: parsedStoreId,
    vendorName: cleanName,
    createIfMissing: false
  });

  if (conflictingVendor?.vendorId && String(conflictingVendor.vendorId) !== String(vendorId)) {
    throw new Error(`"${cleanName}" already resolves to an existing vendor.`);
  }

  const { data: updatedVendor, error: updateError } = await supabaseClient
    .from('vendors')
    .update({ display_name: cleanName })
    .eq('id', vendorId)
    .eq('store_id', parsedStoreId)
    .select('id, store_id, display_name')
    .single();

  if (updateError) throw new Error(updateError.message || 'Failed to rename vendor.');

  await insertVendorAliasIfNeeded({
    supabaseClient,
    parsedStoreId,
    vendorId,
    aliasName: oldName,
    provenance: { source: 'vendor_rename', channel, userId }
  });
  await insertVendorAliasIfNeeded({
    supabaseClient,
    parsedStoreId,
    vendorId,
    aliasName: cleanName,
    provenance: { source: 'vendor_rename', channel, userId }
  });
  clearVendorDirectoryCache(parsedStoreId);

  // Update items already linked by vendor_id (fast path)
  await supabaseClient
    .from('inventory_items')
    .update({ vendor: cleanName })
    .eq('store_id', parsedStoreId)
    .eq('vendor_id', vendorId);

  // Also link and rename items that match the old vendor name as text but have no vendor_id.
  // This covers items imported before the vendor FK was used.
  if (oldName) {
    await supabaseClient
      .from('inventory_items')
      .update({ vendor: cleanName, vendor_id: vendorId })
      .eq('store_id', parsedStoreId)
      .is('vendor_id', null)
      .ilike('vendor', oldName);
  }

  // Legacy table compatibility.
  await supabaseClient
    .from('inventories')
    .update({ vendor: cleanName })
    .eq('store_id', parsedStoreId)
    .eq('vendor_id', vendorId);

  if (oldName) {
    await supabaseClient
      .from('inventories')
      .update({ vendor: cleanName, vendor_id: vendorId })
      .eq('store_id', parsedStoreId)
      .is('vendor_id', null)
      .ilike('vendor', oldName);
  }

  return updatedVendor;
}

export function getVendorMatchState(vendorDirectory, value) {
  const cleanName = normalizeVendorName(value);
  if (!cleanName) return { state: 'empty' };
  const resolved = resolveFromDirectory(vendorDirectory, cleanName);
  if (!resolved) return { state: 'new', input: cleanName };
  if (resolved.matchType === 'alias') {
    return {
      state: 'alias',
      input: cleanName,
      vendorId: resolved.vendorId,
      vendorDisplayName: resolved.vendorDisplayName,
      aliasText: resolved.matchedText
    };
  }
  return {
    state: 'existing',
    input: cleanName,
    vendorId: resolved.vendorId,
    vendorDisplayName: resolved.vendorDisplayName
  };
}

export function searchVendorSuggestions(vendorDirectory, query, limit = 8) {
  const input = normalizeVendorName(query);
  const queryKey = normalizeVendorMatchKey(input);
  if (!queryKey) return [];

  const matches = [];
  const seenIds = new Set();

  (vendorDirectory?.vendors || []).forEach((vendor) => {
    const display = normalizeVendorName(vendor.display_name);
    if (!display) return;
    if (!normalizeVendorMatchKey(display).includes(queryKey)) return;
    const id = `vendor-${vendor.id}`;
    if (seenIds.has(id)) return;
    seenIds.add(id);
    matches.push({
      id,
      vendorId: vendor.id,
      label: display,
      meta: 'Existing vendor',
      matchType: 'vendor'
    });
  });

  (vendorDirectory?.aliases || []).forEach((aliasRow) => {
    if (matches.length >= limit) return;
    const alias = normalizeVendorName(aliasRow.alias);
    if (!alias || !normalizeVendorMatchKey(alias).includes(queryKey)) return;
    const vendor = (vendorDirectory?.vendors || []).find((row) => String(row.id) === String(aliasRow.vendor_id));
    if (!vendor) return;
    const id = `alias-${aliasRow.id || alias}-${vendor.id}`;
    if (seenIds.has(id)) return;
    seenIds.add(id);
    matches.push({
      id,
      vendorId: vendor.id,
      label: normalizeVendorName(vendor.display_name),
      meta: `Alias: ${alias}`,
      matchType: 'alias',
      alias
    });
  });

  return matches
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
    .slice(0, limit);
}

export async function resolveVendorIdForStore({
  supabaseClient,
  storeId,
  vendorName,
  stubNote = 'Auto-created from import',
  useAI = false,
  channel = null,
  userId = null
}) {
  const resolved = await assignVendorToItem({
    supabaseClient,
    storeId,
    vendorName,
    stubNote,
    useAI,
    channel,
    userId
  });
  return resolved?.vendorId || null;
}

export async function mapRowsWithResolvedVendors({
  supabaseClient,
  storeId,
  rows,
  getVendorInput,
  setResolvedVendor,
  stubNote = 'Auto-created from import',
  useAI = false,
  channel = null,
  userId = null
}) {
  if (!Array.isArray(rows) || !getVendorInput || !setResolvedVendor) return rows || [];
  const parsedStoreId = parseStoreId(storeId);
  if (!supabaseClient || !parsedStoreId) return rows;

  let directory = await getCachedVendorDirectory({
    supabaseClient,
    storeId: parsedStoreId,
    forceRefresh: true
  });
  const resolutionCache = new Map();
  const normalizedInputByRow = rows.map((row) => normalizeVendorName(getVendorInput(row)));

  for (const vendorInput of normalizedInputByRow) {
    if (!vendorInput) continue;
    const cacheKey = vendorInput.toLowerCase();
    if (resolutionCache.has(cacheKey)) continue;
    // eslint-disable-next-line no-await-in-loop
    const resolved = await assignVendorToItem({
      supabaseClient,
      storeId: parsedStoreId,
      vendorName: vendorInput,
      stubNote,
      vendorDirectory: directory,
      useAI,
      channel,
      userId
    });
    resolutionCache.set(cacheKey, resolved);
    // Refresh directory only when this row mutated state (created a new vendor
    // or attached a new alias) so later rows in the same batch can match
    // against it — e.g. "Sysco LA" first creates the vendor, then
    // "Sysco Los Angeles" matches via AI and is recorded as an alias.
    // Skip the refresh on plain existing-vendor / existing-alias hits to
    // avoid unnecessary DB churn on large imports.
    const mutatedTypes = new Set(['created', 'created_or_resolved', 'ai_alias']);
    if (useAI && resolved?.vendorId && mutatedTypes.has(resolved.matchType)) {
      // eslint-disable-next-line no-await-in-loop
      directory = await getCachedVendorDirectory({
        supabaseClient,
        storeId: parsedStoreId,
        forceRefresh: true
      });
    }
  }

  return rows.map((row, index) => {
    const inputName = normalizedInputByRow[index];
    if (!inputName) {
      return setResolvedVendor(row, { vendorId: null, vendorDisplayName: null, matchType: 'empty' });
    }
    const resolved = resolutionCache.get(inputName.toLowerCase()) || {
      vendorId: null,
      vendorDisplayName: inputName,
      matchType: 'unresolved'
    };
    return setResolvedVendor(row, resolved);
  });
}
